import React from "react";

interface NavigationItem {
  href: string;
  label: string;
  visible: boolean;
}

interface BottomNavigationProps {
  navigationItems: NavigationItem[];
  activeSection: string;
  navigationRef: React.RefObject<HTMLDivElement>;
}

export const BottomNavigation: React.FC<BottomNavigationProps> = ({
  navigationItems,
  activeSection,
  navigationRef,
}) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-t border-gray-200/50 dark:border-gray-800/50 supports-[backdrop-filter]:bg-white/95 dark:supports-[backdrop-filter]:bg-gray-900/95 safe-area-pb shadow-lg">
      <div className="mx-auto max-w-6xl ">
        <div
          ref={navigationRef}
          className="flex items-center gap-2 overflow-x-auto scrollbar-hide scroll-smooth"
        >
          {navigationItems.map((item, index) => {
            const isActive = activeSection === item.href.replace("#", "");
            return (
              <a
                key={item.href}
                href={item.href}
                className="relative group px-4 py-2 my-2 mx-3 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-all duration-700 ease-out transform"
                style={{
                  backgroundColor: isActive ? "#3b82f6" : "transparent",
                  color: isActive ? "white" : "",
                  boxShadow: isActive
                    ? "0 10px 25px -5px rgba(59, 130, 246, 0.4)"
                    : "0 0 0 0 transparent",
                  transform: isActive ? "scale(1.05)" : "scale(1)",
                  transitionDelay: `${index * 15}ms`,
                }}
              >
                {/* Active background glow with smoother transitions */}
                <div
                  className="absolute -inset-1 rounded-full bg-blue-500/20 opacity-0 transition-all duration-700 ease-out"
                  style={{
                    opacity: isActive ? 0.5 : 0,
                    animation: isActive
                      ? "ping 2s cubic-bezier(0, 0, 0.2, 1) infinite"
                      : "none",
                  }}
                ></div>

                <div
                  className="absolute inset-0 rounded-full bg-blue-500/15 blur-sm opacity-0 transition-all duration-700 ease-out"
                  style={{
                    opacity: isActive ? 1 : 0,
                  }}
                ></div>

                {/* Tab content with smooth color transitions */}
                <span
                  className="relative z-10 flex items-center gap-1.5 transition-all duration-700 ease-out"
                  style={{
                    color: isActive ? "white" : "inherit",
                  }}
                >
                  <span
                    className="transition-all duration-700 ease-out"
                    style={{
                      color: isActive
                        ? "white"
                        : typeof window !== "undefined" &&
                            window.matchMedia("(prefers-color-scheme: dark)")
                              .matches
                          ? "#9ca3af"
                          : "#4b5563",
                    }}
                  >
                    {item.label}
                  </span>
                  <div
                    className="w-1.5 h-1.5 bg-white rounded-full animate-float shadow-sm transition-all duration-500 ease-out"
                    style={{
                      opacity: isActive ? 1 : 0,
                      transform: isActive ? "scale(1)" : "scale(0)",
                    }}
                  ></div>
                </span>

                {/* Hover indicator */}
                <div
                  className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-all duration-400 ease-out rounded-full opacity-0 group-hover:opacity-100"
                  style={{
                    opacity: isActive ? 0 : "",
                  }}
                ></div>

                {/* Hover effects */}
                <div
                  className="absolute inset-0 rounded-full bg-blue-100/50 dark:bg-blue-900/30 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out"
                  style={{
                    opacity: isActive ? 0 : "",
                  }}
                ></div>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};
